import { useLogger } from "@bindery/logging/react";
import { Button } from "@bindery/ui/components/ui/button";
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router";
import {
  readerToForget,
  type SignOutIntent,
  takeSignOutIntent,
} from "@/auth/sign-out-intent";
import { WrongBookmarkScreen } from "@/library/bookmark-identity-gate";
import { browserResetDeps } from "@/chrome/reset-progress";

export const WRONG_BOOKMARK_REASON = "wrong-bookmark";

const forgetLocalReader = (readerId: string): Promise<void> =>
  import("@/sync").then((sync) => sync.forgetLocalReader(readerId));

const clearOfflineReader = (): Promise<void> =>
  import("@/sync").then((sync) => sync.clearOfflineReader());

/**
 * A reader who signed out leaves nothing of theirs on screen for whoever
 * picks the device up next: their mirrors, nickname, milestone flags and
 * unconfirmed marker go, as on reset, and the device no longer resumes
 * them. Their unsent outbox rows stay, as for "Not me":
 * research data that flush only under that reader's own session and expire
 * after 30 days without one.
 */
export async function clearSignedOutReader(
  readerId: string,
  forget: (readerId: string) => Promise<void> = forgetLocalReader,
  forgetDeviceMarks: (
    readerId: string,
  ) => void = browserResetDeps.forgetDeviceMarks,
): Promise<void> {
  forgetDeviceMarks(readerId);
  await forget(readerId);
}

export function SignedOutPage({
  clearReader = clearSignedOutReader,
  clearOffline = clearOfflineReader,
  signOutIntended = takeSignOutIntent,
}: {
  clearReader?: (readerId: string) => Promise<void>;
  clearOffline?: () => Promise<void>;
  signOutIntended?: () => SignOutIntent | null;
}) {
  const [searchParams] = useSearchParams();
  const wrongBookmark = searchParams.get("reason") === WRONG_BOOKMARK_REASON;
  const log = useLogger();

  // The mark is spent on every arrival, so none outlives this page. "Not
  // me" already forgot the bookmark's reader; the device's own stays.
  // Any sign-out, an account's included, stops this device reading offline
  // as the reader it was signed in for.
  useEffect(() => {
    const intent = signOutIntended();
    if (wrongBookmark || intent === null) return;
    const logClearError = (error: unknown) =>
      log.error("auth.sign_out.clear_state.error", error);
    clearOffline().catch(logClearError);
    const readerId = readerToForget(intent);
    if (readerId !== null) clearReader(readerId).catch(logClearError);
  }, [wrongBookmark, clearReader, clearOffline, signOutIntended, log]);

  if (wrongBookmark) return <WrongBookmarkScreen />;
  return (
    <main className="flex min-h-screen flex-col items-center justify-center p-8 text-center">
      <h1 className="mb-4 text-2xl font-bold">You&apos;re all set!</h1>
      <p className="text-muted-foreground mb-6">
        Thanks for reading with us today. Your reading adventure is always
        waiting when you&apos;re ready to return.
      </p>
      <Button asChild size="lg">
        <Link to="/">Back to Home</Link>
      </Button>
    </main>
  );
}
